import { EnsuredQueryKey, InfiniteData, UseMutationOptions, useQueryClient } from 'react-query';
import produce from 'immer';
import { v4 as uuidv4 } from 'uuid';

import {
  ContentApproval,
  ContentFragment,
  FeedKey,
  GetFeedQuery,
  GetFeedQueryVariables,
  ProcessContentV2Mutation,
  ProcessContentV2MutationVariables,
  useGetFeedQuery,
} from 'api';
import { useCurrentUser, useMonitorPost, useUserPreferenceStorage } from 'hooks';
import {
  DEFAULT_SORT,
  FEED_LIMIT_LIST,
  LOCAL_STORAGE_KEY_FEED_SORT,
  USER_FEED_ID,
} from 'app-constants';

type Mutation = ProcessContentV2Mutation;
type MutationVariables = ProcessContentV2MutationVariables;
type QueryData = InfiniteData<GetFeedQuery>;
type GroupQueryKeys = EnsuredQueryKey<(string | GetFeedQueryVariables)[]>[];

const ID_STUB = uuidv4();

const useCreatePost = () => {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const { dispatch } = useMonitorPost();
  const [sort] = useUserPreferenceStorage(LOCAL_STORAGE_KEY_FEED_SORT, DEFAULT_SORT);

  const common = {
    sort,
    limit: FEED_LIMIT_LIST,
  };

  const timelineQueryKey = useGetFeedQuery.getKey({
    id: currentUser.id,
    key: FeedKey.Timeline,
    ...common,
  });

  const profileQueryKey = useGetFeedQuery.getKey({
    id: currentUser.id,
    key: FeedKey.Alluser,
    ...common,
  });

  const activityQueryKey = useGetFeedQuery.getKey({
    id: currentUser.id,
    key: FeedKey.User,
    ...common,
  });

  const groupQueryKeys: GroupQueryKeys = [];

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: ({ content }) => {
      content.group_ids.forEach(groupId => {
        if (groupId === USER_FEED_ID) {
          return;
        }

        groupQueryKeys.push(
          useGetFeedQuery.getKey({
            id: groupId,
            key: FeedKey.Group,
            ...common,
          })
        );
      });

      const queryKeys = [timelineQueryKey, activityQueryKey, profileQueryKey].concat(
        groupQueryKeys
      );

      const newContent: ContentFragment = {
        ...content,
        content_id: ID_STUB,
        publish_at: content.publish_at,
        group_id_approvals: [],
        pinned_to_groups: [],
        content_object: content.content_object,
      };

      queryKeys.map(async queryKey => {
        await queryClient.cancelQueries(queryKey);

        const baseState = queryClient.getQueryData<QueryData>(queryKey);

        const nextState =
          baseState &&
          produce(baseState, draftState => {
            if (!content.schedule?.publish) {
              draftState.pages[0].getFeed.results.unshift(newContent);
            }
          });

        if (baseState) {
          queryClient.setQueryData<QueryData>(queryKey, nextState);
        }
      });
    },
    onSettled: ({ processContentV2 }) => {
      const queryKeys = [timelineQueryKey, activityQueryKey, profileQueryKey].concat(
        groupQueryKeys
      );

      queryKeys.map(async queryKey => {
        await queryClient.cancelQueries(queryKey);

        const baseState = queryClient.getQueryData<QueryData>(queryKey);
        const nextState =
          baseState &&
          produce(baseState, draftState => {
            const [firstPage] = draftState.pages;
            const index = firstPage.getFeed.results.findIndex(
              result => result.content_id === ID_STUB
            );
            draftState.pages[0].getFeed.results[index] = processContentV2.content;
            if (draftState.pages[0].getFeed.results[index].approval === ContentApproval.Submitted) {
              delete draftState.pages[0].getFeed.results[index];
            }
          });

        if (baseState) {
          queryClient.setQueryData<QueryData>(queryKey, nextState);
          dispatch({ type: 'watch', id: processContentV2.content.content_id });
        }
      });
    },
  };

  return mutationOptions;
};

export default useCreatePost;
